import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Save, GripVertical, Library, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Card } from '../ui/card';
import { api } from '../../api/feedy';
import { saveCollection } from '../../utils/saveCollection';
import { ConditionsEditor, ActionsEditor } from './RuleBuilder';

const emptyRule = () => ({
  name: '', enabled: true,
  condition: { match: 'all', conditions: [{ field: '', operator: 'equals', value: '' }] },
  action: { type: 'exclude' },
});

export default function RulesTab({ feed, internalFields, onSaved, focusRuleId }) {
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState([]);
  const [libOpen, setLibOpen] = useState(false);
  const originalIds = useRef([]);

  useEffect(() => { api.rulePresets.list().then(setPresets).catch(() => {}); }, []);

  const addPreset = (p) => {
    setRules((rs) => [...rs, { name: p.name, enabled: true, condition: p.condition, action: p.action }]);
    setLibOpen(false);
  };

  useEffect(() => {
    const init = [...(feed.rules || [])].sort((a, b) => a.order - b.order).map((r) => ({
      ...r,
      condition: r.condition?.conditions ? r.condition : emptyRule().condition,
      action: r.action ?? { type: 'exclude' },
    }));
    setRules(init);
    originalIds.current = init.filter((r) => r.id).map((r) => r.id);
  }, [feed.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opened from the Excluded tab: bring that rule into view and highlight it.
  useEffect(() => {
    if (!focusRuleId) return;
    const t = setTimeout(() => document.getElementById(`feed-rule-${focusRuleId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
    return () => clearTimeout(t);
  }, [focusRuleId, rules.length]);

  const patchRule = (i, patch) => setRules((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRule = () => setRules((rs) => [...rs, emptyRule()]);
  const removeRule = (i) => setRules((rs) => rs.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const res = await saveCollection(rules, originalIds.current, {
        payload: (r, idx) => ({
          feed: feed.id, name: r.name, order: idx, enabled: r.enabled,
          condition: r.condition, action: r.action,
        }),
        create: api.rules.create, update: api.rules.update, remove: api.rules.remove,
      });
      setRules(res.rows);
      originalIds.current = res.ids;
      if (!res.error) await onSaved();  // failures were already shown as a toast
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Rules run top-to-bottom; they can filter on internal fields and read/modify output fields.</div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" className="gap-1.5" onClick={() => setLibOpen((o) => !o)}>
            <Library className="h-4 w-4" /> Library <ChevronDown className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" className="gap-1.5" onClick={save} disabled={saving}><Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save rules'}</Button>
        </div>
      </div>

      {libOpen && (
        <Card className="p-2">
          <p className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Rule library</p>
          <div className="grid gap-1 sm:grid-cols-2">
            {presets.map((p) => (
              <button key={p.key} onClick={() => addPreset(p)} className="flex items-start gap-2 rounded-md px-2.5 py-2 text-left hover:bg-muted">
                <Plus className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>
                  <span className="block text-sm font-medium">{p.name}</span>
                  <span className="block text-xs text-muted-foreground">{p.description}</span>
                </span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {rules.length === 0 && (
        <Card className="p-8 text-center text-sm text-muted-foreground">No rules yet. Add one to filter or transform items.</Card>
      )}

      <div className="space-y-3">
        {rules.map((rule, ri) => (
          <Card key={rule.id ?? `new-${ri}`} id={rule.id ? `feed-rule-${rule.id}` : undefined}
            className={`p-4 ${rule.id && rule.id === focusRuleId ? 'ring-2 ring-primary' : ''}`}>
            <div className="flex items-center gap-2">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
              <Input
                value={rule.name}
                onChange={(e) => patchRule(ri, { name: e.target.value })}
                placeholder={`Rule ${ri + 1}`}
                className="h-9 max-w-xs font-medium"
              />
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Enabled</span>
                <Switch checked={rule.enabled} onCheckedChange={(v) => patchRule(ri, { enabled: v })} />
                <Button variant="ghost" size="icon" onClick={() => removeRule(ri)} aria-label="Delete rule"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>

            <div className="mt-3 rounded-md border-l-4 border-l-amber-400 bg-amber-500/[0.04] p-3">
              <div className="mb-2 text-sm font-semibold">IF</div>
              <ConditionsEditor condition={rule.condition} fields={internalFields} onChange={(c) => patchRule(ri, { condition: c })} />
            </div>

            <div className="mt-3 rounded-md border-l-4 border-l-sky-400 bg-sky-500/[0.04] p-3">
              <div className="mb-2 text-sm font-semibold">THEN</div>
              <ActionsEditor action={rule.action} fields={internalFields} onChange={(a) => patchRule(ri, { action: a })} />
            </div>
          </Card>
        ))}
      </div>

      <Button variant="outline" size="sm" className="gap-1" onClick={addRule}><Plus className="h-4 w-4" /> Add rule</Button>
    </div>
  );
}
